// Umsatzauswertungen: Umsatz nach Zeit, Standort, Vertriebsweg, Warengruppe, Artikel, Kunde – mit Vergleich und dem
// Weg zu den Belegen –, Renner und Penner mit ABC-Klassen, Kassenauswertung nach Stunde, Wochentag und Kassenkraft.
//
// Was „Umsatz“ heißt (überall gleich): der Nettoerlös aus festgeschriebenen Rechnungen und Kassenbons, abzüglich
// Rechnungskorrekturen, Kassenrücknahmen und Stornobelegen. Pfand ist kein Umsatz und steht getrennt; ein verkaufter
// Wertgutschein ist kein Umsatz. Rechnungen zählen am Belegdatum, Kassenbons am Geschäftstag ihrer Schicht – wie im
// Buchungsjournal, mit dem jede Summe auf den Cent übereinstimmt.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { SALES_CHANNELS, SALES_GROUPINGS } from '../lib/labels'
import { TIME_BUCKETS } from '../lib/period'
import { compareField, costFields, csvField, locationFields, periodFields, READ_SALES, reportScope, zCsv, zDataState, zDay, zIdOrNone, zPeriod } from './common'

const COST_FIELDS = ['costCents', 'marginCents', 'marginBp'] as const

/** Kennzahlen eines Ausschnitts. Kosten und Rohertrag nur mit `costs.read`. */
export const zSalesMeasures = z.object({
  /** Nettoumsatz in Cent (ohne Pfand, ohne Steuer) */
  revenueCents: z.int(),
  taxCents: z.int(),
  /** verkaufte Menge in Tausendstel der Basiseinheit; null, wenn Artikel mit verschiedenen Einheiten zusammenkommen */
  soldQty: z.int().nullable(),
  /** Kurzform der Einheit zu soldQty */
  unit: z.string().nullable(),
  /** gewährte Nachlässe netto (Zeilenrabatte und Belegrabatte) */
  discountCents: z.int(),
  /** Nachlass in Basispunkten des Umsatzes vor Nachlass */
  discountShareBp: z.int(),
  /** Pfand netto: berechnet abzüglich erstattet – kein Umsatz */
  depositCents: z.int(),
  /** Wareneinsatz zum gleitenden Durchschnitt */
  costCents: z.int().optional(),
  marginCents: z.int().optional(),
  /** Rohertrag in Basispunkten des Umsatzes; null ohne Umsatz */
  marginBp: z.int().nullable().optional(),
})

const zSalesRow = zSalesMeasures.extend({
  /** Schlüssel der Zeile: Tag bzw. erster Tag der Woche/des Monats, sonst die Kennung (oder 'none') */
  key: z.string(),
  label: z.string(),
  sublabel: z.string().nullable(),
  /** Anteil am Umsatz des Zeitraums in Basispunkten */
  shareBp: z.int(),
  comparisonRevenueCents: z.int().nullable(),
  /** Veränderung des Umsatzes gegenüber dem Vergleich in Basispunkten; null ohne Vergleichswert */
  changeBp: z.int().nullable(),
})

const salesFilters = {
  channel: z.enum(SALES_CHANNELS, { error: 'Bitte einen Vertriebsweg aus der Liste wählen.' }).optional(),
  itemGroupId: zIdOrNone.optional(),
  itemId: zIdOrNone.optional(),
  /** Kunde; 'none' = Verkäufe ohne Kunde (Barverkauf) */
  customerId: zIdOrNone.optional(),
  /** Kundengruppe (Preisgruppe des Kunden); 'none' = ohne */
  customerGroupId: zIdOrNone.optional(),
}

export const salesReport = defineContract({
  name: 'reporting.sales.report',
  title: 'Umsatz auswerten',
  description: 'Nettoumsatz, Menge, Nachlass und – mit dem Recht dazu – Rohertrag eines Zeitraums, gruppiert und mit Vergleichszeitraum.',
  llm: {
    description:
      'Wertet den Umsatz eines Zeitraums aus: Summen, Vergleich mit Vorperiode oder Vorjahr und eine Zeile je Gruppe (`groupBy`: day, week, month, location, channel, itemGroup, item, customer, customerGroup). ' +
      'Zeitraum als `period` (today, yesterday, thisWeek, lastWeek, thisMonth, lastMonth, last7, last30, last90, thisYear) ODER `from`/`to`; Standorte über `locationIds`. ' +
      'Beträge in Cent netto (durchlaufende Beträge sind kein Umsatz und stehen getrennt in `depositCents`), Mengen in Tausendstel der Basiseinheit, Anteile und Veränderungen in Basispunkten (620 = 6,2 %). ' +
      'Verwenden für Fragen wie „Wie war der Umsatz in einer Filiale letzte Woche im Vergleich zur Vorwoche?“ (`period: lastWeek`, `compare: previous`, `groupBy: location`). ' +
      'Nicht für einzelne Belege (reporting.sales.documents), nicht für Renner und Penner (reporting.sales.movers), nicht für offene Posten (reporting.finance.report).',
    keywords: ['Umsatz', 'Rohertrag', 'Marge', 'Vorwoche', 'Vormonat', 'Filialvergleich', 'Absatz', 'Tagesumsatz'],
    examples: [{ period: 'lastWeek', compare: 'previous', groupBy: 'location' }],
  },
  input: z.strictObject({
    ...periodFields,
    ...compareField,
    ...locationFields,
    ...salesFilters,
    groupBy: z.enum(SALES_GROUPINGS, { error: 'Bitte eine Gruppierung aus der Liste wählen.' }).default('day'),
    /** höchste Zahl an Zeilen (nach Umsatz, bei Zeitgruppen alle); der Rest steht in `rest` */
    limit: z.int().min(1).max(500).default(100),
    ...csvField,
  }),
  output: z.object({
    period: zPeriod,
    comparison: zPeriod.nullable(),
    /** „ggü. Vorwoche“ */
    comparisonLabel: z.string().nullable(),
    groupBy: z.enum(SALES_GROUPINGS),
    groupByLabel: z.string(),
    totals: zSalesMeasures.extend({
      /** Rechnungen und Kassenbons im Zeitraum (Korrekturen und Rücknahmen nicht mitgezählt) */
      documentCount: z.int(),
      /** Nettoumsatz je Beleg; null ohne Belege */
      averageDocumentCents: z.int().nullable(),
    }),
    comparisonTotals: zSalesMeasures.extend({ documentCount: z.int(), averageDocumentCents: z.int().nullable() }).nullable(),
    /** Veränderung des Umsatzes in Basispunkten; null ohne Vergleichswert */
    changeBp: z.int().nullable(),
    rows: z.array(zSalesRow),
    /** Zeilen jenseits von `limit`, zusammengefasst; null, wenn alle Zeilen gezeigt sind */
    rest: z.object({ rowCount: z.int(), revenueCents: z.int() }).nullable(),
    /** Umsatz je Steuersatz – stimmt mit den Erlösbuchungen des Journals überein */
    taxes: z.array(z.object({ rateBp: z.int(), netCents: z.int(), taxCents: z.int(), grossCents: z.int() })),
    /** Zeitreihe für das Diagramm, mit dem Vergleichszeitraum an derselben Stelle */
    series: z.array(
      z.object({
        date: zDay,
        revenueCents: z.int(),
        comparisonRevenueCents: z.int().nullable(),
        marginBp: z.int().nullable().optional(),
      }),
    ),
    seriesBucket: z.enum(TIME_BUCKETS),
    data: zDataState,
    csv: zCsv.optional(),
  }),
  permission: READ_SALES,
  risk: 'read',
  scope: reportScope,
  fieldPermissions: { ...costFields(['totals', 'comparisonTotals', 'rows.*'], COST_FIELDS), ...costFields(['series.*'], ['marginBp']) },
})

export const salesDocuments = defineContract({
  name: 'reporting.sales.documents',
  title: 'Belege hinter dem Umsatz anzeigen',
  description: 'Die Rechnungen, Rechnungskorrekturen und Kassenbons, aus denen sich eine Umsatzzahl zusammensetzt.',
  llm: {
    description:
      'Listet die Belege hinter einer Umsatzzahl: Rechnungen, Rechnungskorrekturen, Kassenbons und Kassenrücknahmen eines Zeitraums mit ihrem Beitrag zum Nettoumsatz (Cent), größter Betrag zuerst. ' +
      'Dieselben Filter wie reporting.sales.report (Standorte, Vertriebsweg, Warengruppe, Artikel, Kunde, Kundengruppe). ' +
      'Verwenden, wenn jemand wissen will, woraus sich ein Umsatz zusammensetzt. Nicht für die Summe selbst (reporting.sales.report).',
    keywords: ['Belege zum Umsatz', 'welche Rechnungen', 'welche Bons', 'Nachweis'],
  },
  input: z.strictObject({
    ...periodFields,
    ...locationFields,
    ...salesFilters,
    limit: z.int().min(1).max(500).default(100),
  }),
  output: z.object({
    period: zPeriod,
    rows: z.array(
      z.object({
        id: zId,
        type: z.string(),
        typeLabel: z.string(),
        number: z.string().nullable(),
        /** Tag, an dem der Beleg in den Umsatz zählt */
        date: zDay,
        location: z.object({ id: zId, code: z.string() }),
        customerName: z.string().nullable(),
        /** Beitrag dieses Belegs zum Nettoumsatz unter den gewählten Filtern (Korrekturen negativ) */
        revenueCents: z.int(),
        depositCents: z.int(),
      }),
    ),
    totals: z.object({ documentCount: z.int(), revenueCents: z.int() }),
    /** true: Es gibt mehr Belege als `limit`; die Summen gelten trotzdem für alle */
    truncated: z.boolean(),
  }),
  permission: READ_SALES,
  risk: 'read',
  scope: reportScope,
})

export const MOVER_BASES = ['revenue', 'quantity', 'margin'] as const
export const MOVER_VIEWS = ['top', 'slow'] as const

export const salesMovers = defineContract({
  name: 'reporting.sales.movers',
  title: 'Renner und Penner anzeigen',
  description: 'Artikel nach Umsatz, Menge oder Rohertrag mit ABC-Klasse, Bestand und Reichweite – die stärksten oder die schwächsten zuerst.',
  llm: {
    description:
      'Rangliste der Artikel eines Zeitraums mit ABC-Klasse (A = die Artikel, die zusammen 80 % bringen, B bis 95 %, C der Rest), Anteil, Bestand und Reichweite in Tagen. ' +
      '`view: top` zeigt die Renner, `view: slow` die Penner (Artikel mit Bestand und wenig oder keinem Absatz). `basis`: revenue (Nettoumsatz), quantity (Menge) oder margin (Rohertrag, nur mit dem Recht auf Einstandspreise). ' +
      'Beträge in Cent, Mengen in Tausendstel der Basiseinheit. Verwenden für „Was läuft am besten?“, „Welche Artikel verkaufen sich kaum?“. ' +
      'Nicht für Artikel ganz ohne Bewegung (reporting.stock.deadStock).',
    keywords: ['Renner', 'Penner', 'Topseller', 'ABC-Analyse', 'meistverkauft', 'Langsamdreher'],
    examples: [{ period: 'last30', view: 'top', basis: 'revenue' }],
  },
  input: z.strictObject({
    ...periodFields,
    ...locationFields,
    itemGroupId: zIdOrNone.optional(),
    basis: z.enum(MOVER_BASES, { error: 'Bitte eine Grundlage aus der Liste wählen.' }).default('revenue'),
    view: z.enum(MOVER_VIEWS, { error: 'Bitte Renner oder Penner wählen.' }).default('top'),
    limit: z.int().min(1).max(500).default(50),
    ...csvField,
  }),
  output: z.object({
    period: zPeriod,
    basis: z.enum(MOVER_BASES),
    view: z.enum(MOVER_VIEWS),
    totals: z.object({
      /** Artikel mit Absatz im Zeitraum */
      soldItemCount: z.int(),
      /** Artikel mit Bestand, aber ohne Absatz im Zeitraum */
      unsoldItemCount: z.int(),
      revenueCents: z.int(),
    }),
    classes: z.array(z.object({ abc: z.enum(['A', 'B', 'C']), itemCount: z.int(), revenueCents: z.int(), shareBp: z.int() })),
    rows: z.array(
      z.object({
        itemId: zId,
        sku: z.string(),
        name: z.string(),
        groupName: z.string().nullable(),
        /** Rang nach der gewählten Grundlage; null ohne Absatz */
        rank: z.int().nullable(),
        abc: z.enum(['A', 'B', 'C']),
        shareBp: z.int(),
        cumulativeBp: z.int(),
        soldQty: z.int(),
        unit: z.string(),
        revenueCents: z.int(),
        costCents: z.int().optional(),
        marginCents: z.int().optional(),
        marginBp: z.int().nullable().optional(),
        /** Bestand heute an den gewählten Standorten */
        onHandQty: z.int(),
        /** Reichweite in Tagen beim Absatz des Zeitraums; null ohne Absatz */
        daysOfSupply: z.int().nullable(),
        /** Umsatz je Woche des Zeitraums (älteste zuerst) – für die Verlaufslinie */
        trend: z.array(z.int()),
      }),
    ),
    data: zDataState,
    csv: zCsv.optional(),
  }),
  permission: READ_SALES,
  risk: 'read',
  scope: reportScope,
  fieldPermissions: costFields(['rows.*'], COST_FIELDS),
})

export const tillAnalysis = defineContract({
  name: 'reporting.till.analysis',
  title: 'Kasse auswerten',
  description: 'Kassenbons nach Stunde und Wochentag, Durchschnittsbon, Stornos, Rücknahmen und Rabatte – auf Wunsch je Kassenkraft.',
  llm: {
    description:
      'Wertet die Kassen eines Zeitraums aus: Zahl der Bons, Umsatz (Cent netto und brutto, ohne durchlaufende Beträge), Durchschnittsbon, Verteilung nach Stunde und Wochentag, Stornos, Rücknahmen und Rabatte. ' +
      'Die Aufschlüsselung je Kassenkraft (`cashiers`) erhält nur, wer an den Standorten Kassenschichten lesen darf. ' +
      'Verwenden für „Wann ist an der Kasse am meisten los?“, „Wie hoch ist der Durchschnittsbon?“. Nicht für den Abschluss einer Schicht (pos.shift.get).',
    keywords: ['Kasse', 'Durchschnittsbon', 'Stoßzeiten', 'Stornos', 'Rabatte', 'Kassenkraft', 'Bons je Stunde'],
    examples: [{ period: 'last30' }],
  },
  input: z.strictObject({ ...periodFields, ...locationFields, ...csvField }),
  output: z.object({
    period: zPeriod,
    totals: z.object({
      receiptCount: z.int(),
      /** Nettoumsatz der Kasse – dieselbe Zahl wie reporting.sales.report mit channel 'pos' */
      revenueCents: z.int(),
      /** Umsatz brutto ohne Pfand */
      revenueGrossCents: z.int(),
      /** Bruttoumsatz ohne Pfand je Bon; null ohne Bons */
      averageReceiptCents: z.int().nullable(),
      returnCount: z.int(),
      returnCents: z.int(),
      /** Zeilenstornos und abgebrochene Bons vor der Zahlung */
      voidCount: z.int(),
      voidCents: z.int(),
      discountCount: z.int(),
      discountCents: z.int(),
    }),
    byHour: z.array(z.object({ hour: z.int(), label: z.string(), receiptCount: z.int(), revenueGrossCents: z.int() })),
    byWeekday: z.array(z.object({ weekday: z.int(), label: z.string(), dayCount: z.int(), receiptCount: z.int(), revenueGrossCents: z.int(), averageReceiptCents: z.int().nullable() })),
    /** Bons je Wochentag und Stunde – nur Felder mit Bons */
    heat: z.array(z.object({ weekday: z.int(), hour: z.int(), receiptCount: z.int() })),
    /** je Kassenkraft; fehlt ohne das Recht, Kassenschichten zu lesen */
    cashiers: z
      .array(
        z.object({
          userId: zId.nullable(),
          name: z.string(),
          receiptCount: z.int(),
          revenueGrossCents: z.int(),
          averageReceiptCents: z.int().nullable(),
          voidCount: z.int(),
          voidCents: z.int(),
          returnCount: z.int(),
          returnCents: z.int(),
          discountCount: z.int(),
          discountCents: z.int(),
        }),
      )
      .optional(),
    data: zDataState,
    csv: zCsv.optional(),
  }),
  permission: READ_SALES,
  risk: 'read',
  scope: reportScope,
  feature: 'pos',
})
